import { Flex, Text } from '@servicetitan/anvil2';
import { Headline } from '@servicetitan/design-system';
import {
    Checkbox,
    Fieldset,
    Radio,
    RadioGroup,
    StateSelectField,
    TextField,
} from '@servicetitan/anvil2-react-hook-form';
import { MailingAddressFields } from '../mailing-address/MailingAddressFields';
import { useFormContext } from 'react-hook-form';
import {
    resolveResidenceOwnershipValueSet,
    useProviderConfig,
    useSecondLookIntegration,
} from '@servicetitan/financing-workflow-shared';
import { IndustryTypeCombobox } from '../common/IndustryTypeCombobox';
import { ImprovementTypeCombobox } from '../common/ImprovementTypeCombobox';
import { SpouseFormFields } from '../spouseInfo';
import { Translate } from '@servicetitan/financing-utils-shared';
import { useEffect } from 'react';

interface JobDetailsFieldsProps {
    onStateChange?: (state: string) => void;
    fieldPrefix?: string;
}

export const JobDetailsFields = ({ onStateChange, fieldPrefix = '' }: JobDetailsFieldsProps) => {
    const { watch, clearErrors } = useFormContext();
    const providerConfig = useProviderConfig();
    const { jobDetailsFieldOverrides, allowMailingAddressOverride } = providerConfig;
    const secondLookIntegration = useSecondLookIntegration();

    // Check if mortgage fields are in the field overrides
    const requireMortgageDetails =
        jobDetailsFieldOverrides?.additions?.some(
            f =>
                f === 'mortgageMonthlyPayment' ||
                f === 'mortgageEstimatedBalance' ||
                f === 'isManufacturedHome',
        ) ?? false;
    const useImprovementTypeInJobDetails =
        jobDetailsFieldOverrides?.additions?.some(f => f === 'improvementType') ?? false;
    const useIndustryTypeInJobDetails =
        jobDetailsFieldOverrides?.additions?.some(f => f === 'industryType') ?? false;

    const residenceFieldName = [fieldPrefix, 'jobDetails.residenceType'].join('.');
    const stateFieldName = [fieldPrefix, 'jobDetails.address.state'].join('.');
    const manufacturedFieldName = [fieldPrefix, 'jobDetails.isManufacturedHome'].join('.');
    const mortgagePaymentFieldName = [fieldPrefix, 'jobDetails.mortgageMonthlyPayment'].join('.');
    const mortgageBalanceFieldName = [fieldPrefix, 'jobDetails.mortgageEstimatedBalance'].join('.');
    const mailingAddressDiffersFieldName = [
        fieldPrefix,
        'jobDetails.mailingAddressDiffersFromJobAddress',
    ].join('.');
    // Watch state to dynamically determine what spouse information we need to gather
    const selectedState = watch(stateFieldName) as string | undefined;
    const residenceType = watch(residenceFieldName);
    const mailingAddressDiffers = watch(mailingAddressDiffersFieldName) as boolean | undefined;

    const applicantType: 'primary' | 'co-applicant' =
        fieldPrefix === 'coApplicant' ? 'co-applicant' : 'primary';

    const handleStateChange = (state: string) => {
        onStateChange?.(state);
    };

    const showMortgage = requireMortgageDetails && residenceType === 'ownWithMortgage';
    const { secondLookProviderConfig } = secondLookIntegration;
    // When a waterfall second-look config is present, use it to widen the residence options.
    // When Finturf is the active provider directly (no waterfall), fall back to the current
    // provider's own fieldValueSets so its extended options (LiveWithParents, Other) are shown.
    const residenceOptions = resolveResidenceOwnershipValueSet(
        secondLookProviderConfig ?? providerConfig,
    ).allowed;

    // Clear validation errors for mortgage fields when they're hidden
    useEffect(() => {
        if (requireMortgageDetails && !showMortgage) {
            clearErrors(mortgagePaymentFieldName);
            clearErrors(mortgageBalanceFieldName);
        }
    }, [
        showMortgage,
        requireMortgageDetails,
        mortgagePaymentFieldName,
        mortgageBalanceFieldName,
        clearErrors,
    ]);

    return (
        <Flex direction="column" gap="6">
            <Flex direction="column" gap="1">
                <Headline size="medium">Job Details</Headline>
                <Text variant="body">Enter the address where the work will be performed</Text>
            </Flex>
            <Fieldset>
                <TextField
                    label="Address Line 1"
                    name={[fieldPrefix, 'jobDetails.address.street'].join('.')}
                    required
                />
                <TextField
                    label="Address Line 2"
                    name={[fieldPrefix, 'jobDetails.address.address2'].join('.')}
                />
                <Flex
                    gap="4"
                    direction="column"
                    sm={{ direction: 'row' }}
                    style={{ width: '100%', gridColumn: '1 / -1' }}
                >
                    <div style={{ flex: '1 1 0', minWidth: 0 }}>
                        <TextField
                            label="City"
                            name={[fieldPrefix, 'jobDetails.address.city'].join('.')}
                            required
                        />
                    </div>
                    <div style={{ flex: '1 1 0', minWidth: 0 }}>
                        <StateSelectField
                            label="State"
                            name={[fieldPrefix, 'jobDetails.address.state'].join('.')}
                            style={{ width: '100%' }}
                            onChange={handleStateChange}
                            required
                        />
                    </div>
                    <div style={{ flex: '1 1 0', minWidth: 0 }}>
                        <TextField
                            label="Zip Code"
                            name={[fieldPrefix, 'jobDetails.address.zip'].join('.')}
                            required
                            style={{ width: '100%' }}
                            inputMode="numeric"
                        />
                    </div>
                </Flex>
                <Flex direction="column" gap="4" style={{ width: '100%', gridColumn: '1 / -1' }}>
                    <RadioGroup legend="Residence Type" name={residenceFieldName} required>
                        {residenceOptions.map(option => (
                            <Radio
                                key={option}
                                name={residenceFieldName}
                                value={option}
                                label={Translate.residenceOwnership(option)}
                            />
                        ))}
                    </RadioGroup>

                    {/* Always render mortgage fields to keep them registered; hide when not applicable */}
                    {requireMortgageDetails && (
                        <Flex
                            gap="4"
                            direction="column"
                            sm={{ direction: 'row' }}
                            style={{
                                width: '100%',
                                gridColumn: '1 / -1',
                                display: showMortgage ? 'flex' : 'none',
                            }}
                        >
                            <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                <TextField
                                    label="Monthly Mortgage Payment"
                                    name={[fieldPrefix, 'jobDetails.mortgageMonthlyPayment'].join(
                                        '.',
                                    )}
                                    prefix="$"
                                    type="number"
                                    inputMode="decimal"
                                    required
                                />
                            </div>
                            <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                <TextField
                                    label="Estimated Mortgage Balance"
                                    name={[fieldPrefix, 'jobDetails.mortgageEstimatedBalance'].join(
                                        '.',
                                    )}
                                    prefix="$"
                                    type="number"
                                    inputMode="decimal"
                                    required
                                />
                            </div>
                        </Flex>
                    )}

                    {/* Show IndustryTypeCombobox when:
                        1. Provider config explicitly adds it (e.g., Turns as primary provider), OR
                        2. Turns integration is enabled for waterfall/second-look flows (unless using ImprovementType)
                    */}
                    {(useIndustryTypeInJobDetails ||
                        (secondLookIntegration.isEnabled && !useImprovementTypeInJobDetails)) &&
                        !secondLookIntegration.isServiceDegraded && (
                            <IndustryTypeCombobox
                                fieldPrefix={[fieldPrefix, 'jobDetails'].join('.')}
                            />
                        )}
                    {/* Show ImprovementTypeCombobox for providers that use it (e.g., ServiceFinance) */}
                    {useImprovementTypeInJobDetails && (
                        <ImprovementTypeCombobox style={{ maxWidth: 420, width: '100%' }} />
                    )}
                    {requireMortgageDetails && (
                        <RadioGroup
                            legend="Is it a mobile or manufactured home?"
                            name={manufacturedFieldName}
                            required
                        >
                            <Radio name={manufacturedFieldName} value label="Yes" type="boolean" />
                            <Radio
                                name={manufacturedFieldName}
                                value={false}
                                label="No"
                                type="boolean"
                            />
                        </RadioGroup>
                    )}
                </Flex>
            </Fieldset>

            {allowMailingAddressOverride && (
                <Flex direction="column" gap="4">
                    {/* The checkbox is always rendered (only the section below is conditional) so
                        its value can't unregister and go stale in the store on uncheck. */}
                    <Checkbox
                        label="Applicant's mailing address is different from the job address"
                        name={mailingAddressDiffersFieldName}
                    />
                    {mailingAddressDiffers && (
                        <MailingAddressFields
                            fieldPrefix={[fieldPrefix, 'jobDetails.mailingAddress'].join('.')}
                        />
                    )}
                </Flex>
            )}

            <SpouseFormFields
                applicantType={applicantType}
                state={selectedState}
                context="job-details"
            />
        </Flex>
    );
};
